import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import Bloco from './componentes/Bloco';
import Colunas from './componentes/Colunas';
import TituloDaTela from './componentes/TituloDaTela';
import IndiceDeSaude from './componentes/IndiceDeSaude';
import { ABAS } from './abas';
import { frasesDaPrimeiraDobra, sinaisMaisGraves } from '../diagnostico/frases';
import { estadoDosDados } from './apresentacao';
import { LARGURA_DE_LEITURA } from './layout';

/**
 * Tela Saúde — a primeira dobra do produto, em FRASES.
 *
 * O que ela NÃO faz, e por quê: não abre com "Tipo 4 de 6". A tipologia
 * Fleuriet é a leitura mais útil que este produto tem para um consultor, e não
 * significa absolutamente nada para o diretor que abriu a tela pela primeira
 * vez — ela mora na tela Caixa e Ciclo, ao lado dos três números que a
 * produzem. Aqui vai o Índice de Saúde, quatro frases com número da própria
 * empresa dentro, e os sinais mais graves com link para o bloco de origem.
 */
const ICONE_DO_NIVEL = {
  excelente: <CheckCircleOutlineIcon fontSize="small" color="success" />,
  bom: <CheckCircleOutlineIcon fontSize="small" color="success" />,
  atencao: <WarningAmberIcon fontSize="small" color="warning" />,
  critico: <ErrorOutlineIcon fontSize="small" color="error" />,
  ausente: <RemoveIcon fontSize="small" sx={{ color: 'text.disabled' }} />,
  semFaixa: <RemoveIcon fontSize="small" sx={{ color: 'text.disabled' }} />
};

const TelaSaude = ({ leitura, onIrPara }) => {
  const frases = frasesDaPrimeiraDobra(leitura);
  const sinais = sinaisMaisGraves(leitura, 3);
  const mesRecente = leitura?.rotulos?.[leitura.rotulos.length - 1] || null;
  const estado = estadoDosDados(leitura);

  const irPara = (tela) => {
    if (onIrPara) onIrPara(tela);
  };

  return (
    <Stack spacing={3} sx={{ maxWidth: LARGURA_DE_LEITURA, mx: 'auto' }}>
      <TituloDaTela
        titulo="Saúde"
        descricao="Minha empresa está saudável, e o que explica essa nota?"
      />
      <IndiceDeSaude leitura={leitura} />

      {/* LEITURA E SINAIS LADO A LADO A PARTIR DE 1200px.
          Empilhados, eles empurravam os sinais para fora da primeira dobra numa
          tela em que sobrava um terço de largura de cada lado. São as duas
          respostas à mesma pergunta — o que está acontecendo, e por quê —, e
          lidas juntas elas se explicam. */}
      <Colunas apartir="lg" minimo={420}>
      <Bloco
        titulo={mesRecente ? `Leitura de ${mesRecente}` : 'Leitura do período'}
        pergunta="Minha empresa está saudável, e por quê em uma frase?"
      >
        {frases.length === 0 ? (
          /* A MENSAGEM TINHA UMA CAUSA SÓ, E EXISTEM DUAS.
             Ela dizia "Lance ao menos um mês do DRE" para QUALQUER leitura
             vazia — inclusive na tela de quem tem doze meses lançados e o
             índice de saúde calculado logo acima, no mesmo cartão que anuncia
             "12 meses informados". Mandar lançar o que já foi lançado faz o
             usuário duvidar do produto, não dos dados dele.

             As frases desta dobra saem de margem líquida em competência, margem
             de segurança e caixa. Quando o lançamento não traz o que elas
             exigem, o que falta não é MÊS: é conta. */
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '65ch' }}>
            {estado.temDRE
              ? 'A leitura em uma frase precisa do resultado em competência, da margem de segurança ou da posição de caixa — e nenhum deles foi medido neste lançamento. Os indicadores que existem estão no Painel, e o que falta para completar está em Dados.'
              : 'Ainda não há número suficiente para uma leitura. Lance ao menos um mês do DRE.'}
          </Typography>
        ) : (
          <Stack spacing={2}>
            {frases.map((frase) => (
              <Stack key={frase.id} direction="row" spacing={1.5} alignItems="flex-start">
                <Box sx={{ mt: 0.25 }}>{ICONE_DO_NIVEL[frase.nivel] || ICONE_DO_NIVEL.semFaixa}</Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body1" sx={{ maxWidth: '65ch' }}>
                    {frase.texto}
                  </Typography>
                  {frase.tela && (
                    <Button
                      size="small"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => irPara(frase.tela)}
                      sx={{ mt: 0.25, ml: -1 }}
                    >
                      {frase.tela === ABAS.DADOS ? 'Completar os dados' : 'Ver de onde vem'}
                    </Button>
                  )}
                </Box>
              </Stack>
            ))}
          </Stack>
        )}
      </Bloco>

      <Bloco
        titulo="Sinais"
        pergunta="O que as regras encontraram sozinhas, sem interpretação."
        acao={
          <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => irPara(ABAS.ANALISE)}>
            Ver todos
          </Button>
        }
        nota="Sinais dizem O QUE está acontecendo e são calculados aqui, sem enviar nada para fora. A Análise do Consultor diz POR QUE e o que atacar primeiro."
      >
        {sinais.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhum sinal de risco disparou no período analisado.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {sinais.map((sinal, indice) => (
              <Stack key={`${sinal.severidade}-${indice}`} direction="row" spacing={1.5} alignItems="flex-start">
                <Box sx={{ mt: 0.25 }}>
                  {sinal.severidade === 'critical'
                    ? ICONE_DO_NIVEL.critico
                    : ICONE_DO_NIVEL.atencao}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body1" sx={{ maxWidth: '65ch' }}>
                    {sinal.mensagem}
                  </Typography>
                  {sinal.recomendacao && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, maxWidth: '65ch' }}>
                      {sinal.recomendacao}
                    </Typography>
                  )}
                </Box>
              </Stack>
            ))}
          </Stack>
        )}
      </Bloco>
      </Colunas>
    </Stack>
  );
};

export default TelaSaude;
